import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { PostsService } from '~/services/domain/PostsService';
import { PostCard } from '@/components/domain/posts/PostCard';
import { VStack } from '@/components/gluestack/vstack';
import { MessageView } from '@/components/ui/MessageView';
import { Alert02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { router } from 'expo-router';
import { NotFoundContent } from '~/components/ui/NotFoundContent';
import { CardWrapper } from '~/components/domain/posts/CardWrapper';
import { ROUTES } from '@/constants/routes';
import { ScrollView } from 'react-native';

interface SinglePostScreenProps {
  postId: number;
}

export function SinglePostScreen({ postId }: SinglePostScreenProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    data: post,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['post', postId],
    queryFn: async () => await PostsService.getPost(postId),
  });

  const handlePostDeleted = useCallback(() => {
    // Remove the post from the query cache
    queryClient.setQueryData(['post', postId], null);

    // Also invalidate any feeds that might contain this post
    queryClient.invalidateQueries({
      queryKey: ['posts'],
      exact: false,
    });

    // Navigate back to the feed
    router.navigate(ROUTES.FEEDS_GLOBAL);
  }, [queryClient, postId]);

  return (
    <VStack className="flex-1">
      <ScreenHeader
        options={{
          title: t('post.singlePost'),
        }}
      />
      {error && error.message !== 'Sequence contains no elements' ? (
        <MessageView
          type="error"
          text={error instanceof Error ? error.message : t('common.errorOccurred')}
          icon={Alert02Icon}
        />
      ) : isLoading ? (
        <CardWrapper>
          <PostCard />
        </CardWrapper>
      ) : post ? (
        <ScrollView>
          <CardWrapper>
            <PostCard post={post} noTruncate={true} onPostDeleted={handlePostDeleted} />
          </CardWrapper>
        </ScrollView>
      ) : (
        <NotFoundContent />
      )}
    </VStack>
  );
}
